import * as React from 'react';
import { connect } from 'react-redux';
import { localize } from 'core/localization';
import progressStorage from 'core/progressStorage';
import { getEmail, getName } from 'store/user/selectors';
import { BUTTON, TOOLTIP, SIZES } from 'constants/components';
import { KEYS } from 'constants/common';
import { withTheme } from 'styled-components';
import { RootAppState } from 'store/types';
import LearnerAgreements from 'components/login/LearnerAgreements';
import AuthForm from '../AuthForm';
import EmailInput from '../AuthForm/EmailInput';
import NameInput from '../AuthForm/NameInput';
import PasswordInput from '../AuthForm/PasswordInput';
import { FormContentWrapper } from '../AuthForm/AuthForm.styled';
import {
  ButtonSubmitSingUp,
  InputContainer,
  CheckboxContainer,
  AgreementCheckBox,
  AgreementLabel
} from './SignUpForm.styled';
import { MessageText, MessageTextWrapper } from '../LoginForm/LoginForm.styled';
import { StatusTooltip, SwitchButton } from '../../common';
import { EMAIL_REGEX, PASSWORD_REGEX } from '../../../constants/common';

type SignUpProps = {
  onSubmit(e: any, data: any): Promise<void>;
  userEmail?: string;
  userName?: string;
  formId: string;
  validationStatusBeforeSubmit?: any;
  isUnknownError: boolean;
  theme?: any;
  resetErrorStatus(): void;
  setFieldRefs?(fieldRefs: any): void;
};

type SignUpState = {
  isUserExist: boolean;
  agreementAccepted: boolean;
};

export class SignUp extends React.Component<SignUpProps, SignUpState> {
  templateTheme: any;

  constructor(props: SignUpProps) {
    super(props);
    this.templateTheme = props.theme;
    this.state = {
      isUserExist: false,
      agreementAccepted: false
    };
  }

  checkIsUserExist = async (email: string) => {
    const emailInputBox: any = document.getElementById('email');
    if (email && EMAIL_REGEX.test(email)) {
      const status = await progressStorage.userExists(email.toLowerCase());
      this.setState({ isUserExist: status.exists });
      emailInputBox.classList.add('valid');
    } else {
      this.setState({ isUserExist: false });
      emailInputBox.classList.remove('valid');
    }
  };

  signUpCommonErrorMessage = () => (
    <MessageTextWrapper>
      <MessageText theme={this.templateTheme} appearance="span">
        {localize('[error signup common]')}
      </MessageText>
    </MessageTextWrapper>
  );

  renderSignUpButton = () => (
    <ButtonSubmitSingUp layout={BUTTON.LAYOUT.PRIMARY} suffixIcon="arrow-right" type="submit">
      {localize('[sign up button]')}
    </ButtonSubmitSingUp>
  );

  renderWithTooltip = (
    status: any,
    showTooltip: any,
    message: any,
    actionElement: any,
    interactive: boolean
  ) => (
    <StatusTooltip
      popoverPosition={'top'}
      layout={status}
      showTooltip={showTooltip}
      message={message}
      popoverButton={actionElement}
      transitionDuration={0.2}
      eventHandler={'click'}
      interactive={interactive}
    />
  );

  renderContent = (props: any) => {
    const { userEmail = '', userName = '', resetErrorStatus } = this.props;
    const {
      onChange,
      setRefs,
      validationStatusBeforeSubmit,
      email,
      isUserExist,
      isUnknownError,
      agreementAccepted
    } = props;
    return (
      <FormContentWrapper>
        <InputContainer size={40}>
          <NameInput
            userName={userName}
            onChange={onChange}
            setRefs={setRefs}
            validationStatusBeforeSubmit={validationStatusBeforeSubmit.name}
            onFocus={resetErrorStatus}
          />
        </InputContainer>
        <InputContainer size={40}>
          <EmailInput
            userEmail={userEmail}
            isUserExist={isUserExist}
            checkIsUserExist={this.checkIsUserExist}
            onChange={onChange}
            setRefs={setRefs}
            email={email}
            textError={
              isUserExist && email ? localize('[user exist]') : localize('[error email input]')
            }
            validationStatusBeforeSubmit={validationStatusBeforeSubmit.email}
            onFocus={resetErrorStatus}
            pattern={EMAIL_REGEX.source}
          />
        </InputContainer>
        <InputContainer size={17}>
          <PasswordInput
            onChange={onChange}
            setRefs={setRefs}
            validationStatusBeforeSubmit={validationStatusBeforeSubmit.password}
            pattern={PASSWORD_REGEX}
            showValidationMessage={true}
            onFocus={resetErrorStatus}
          />
        </InputContainer>
        <CheckboxContainer
          isChecked={agreementAccepted}
          validationStatusBeforeSubmit={validationStatusBeforeSubmit.agreement}
        >
          <AgreementCheckBox
            checked={agreementAccepted}
            id={'agreementCheckbox'}
            name={'agreement'}
            ref={(node: any) => setRefs(node)}
            onChange={() => this.setState({ agreementAccepted: !agreementAccepted })}
          />
          <SwitchButton
            multiple={true}
            selected={agreementAccepted}
            aria-checked={agreementAccepted}
            size={SIZES.SMALL}
            tabIndex={0}
            onClick={() => this.setState({ agreementAccepted: !agreementAccepted })}
            onKeyDown={(event: any) => {
              if (
                [KEYS.ENTER.toLowerCase(), KEYS.SPACEBAR.toLowerCase()].includes(
                  event.key.toLowerCase()
                )
              ) {
                event.preventDefault();
                this.setState({ agreementAccepted: !agreementAccepted });
              }
            }}
          />
          <AgreementLabel htmlFor={'agreementCheckbox'}>
            <LearnerAgreements isSignUpPage={true} />
          </AgreementLabel>
        </CheckboxContainer>
        {this.renderWithTooltip(
          TOOLTIP.INCORRECT,
          isUnknownError,
          this.signUpCommonErrorMessage(),
          this.renderSignUpButton(),
          false
        )}
      </FormContentWrapper>
    );
  };

  render() {
    const { isUserExist, agreementAccepted } = this.state;
    const { onSubmit, formId, isUnknownError, setFieldRefs } = this.props;

    return (
      <AuthForm
        isUserExist={isUserExist}
        checkIsUserExist={this.checkIsUserExist}
        formId={formId}
        onSubmit={onSubmit}
        renderContent={this.renderContent}
        isUnknownError={isUnknownError}
        setFieldRefs={setFieldRefs}
        agreementAccepted={agreementAccepted}
      />
    );
  }
}

function mapStateToProps(state: RootAppState) {
  return {
    userEmail: getEmail(state),
    userName: getName(state)
  };
}

export default withTheme(connect(mapStateToProps)(SignUp));
